/**
 * A ConnectChat service's websites and apps — what its "senders" are.
 *
 * An account manager can set a client up end to end here: add its website,
 * list the domains the widget may run on, pause it, remove it, and hand the
 * client its install snippet. How the widget looks and what it says, and the
 * secret its customers are verified with, are the client's own, in its portal.
 *
 * A site is its service's sender, which is why this replaces the sender list on
 * a ConnectChat service and is guarded by the sender permissions.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Pause, Pencil, Play, Plus, Smartphone, Trash2 } from 'lucide-react';
import {
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  Input,
  Modal,
  QueryPending,
  Select,
  Textarea,
  api,
  formatDateTime,
  toast,
  type Tone,
} from '@connect/web-shared';

import { messagingKeys, type ClientChannel } from './types';

interface Site {
  public_id: string;
  name: string;
  platform: 'website' | 'app';
  site_key: string;
  domains: string[];
  status: 'active' | 'paused';
  health: {
    state: 'never' | 'connected' | 'refused';
    last_seen_at: string | null;
    last_origin: string | null;
    sdk_version: string | null;
    last_refused_origin: string | null;
  };
  open_conversations: number;
}

interface Overview {
  sites: Site[];
  usage: { conversations: number; monthly_conversations: number | null; sites: number; max_sites: number };
  api_url: string;
  sdk_version: string;
  script_url: string | null;
  script_integrity: string | null;
}

const HEALTH: Record<Site['health']['state'], { label: string; tone: Tone }> = {
  never: { label: 'Not seen yet', tone: 'neutral' },
  connected: { label: 'Connected', tone: 'success' },
  refused: { label: 'Refused on an address', tone: 'warning' },
};

function snippet(overview: Overview, site: Site): string {
  // The API's public address (`API_PUBLIC_URL`, `/v1` included); else this host's `/api/v1`.
  const apiUrl = overview.api_url || `${window.location.origin}/api/v1`;
  if (overview.script_url) {
    const integrity = overview.script_integrity
      ? `\n  integrity="${overview.script_integrity}"\n  crossorigin="anonymous"`
      : '';
    return `<script\n  async\n  src="${overview.script_url}"${integrity}\n  data-site-key="${site.site_key}"\n  data-api-url="${apiUrl}"\n></script>`;
  }
  return `import { init } from '@erp-connect/connectchat';\n\ninit({ siteKey: '${site.site_key}', apiUrl: '${apiUrl}' });`;
}

export function ConnectChatSitesCard({ channel }: { channel: ClientChannel }) {
  const queryClient = useQueryClient();
  const base = `/messaging/channels/${channel.public_id}/connectchat`;
  const key = [...messagingKeys.senders(channel.public_id), 'connectchat'] as const;
  const overview = useQuery({ queryKey: key, queryFn: () => api.get<Overview>(base) });
  const [editing, setEditing] = useState<Site | 'new' | null>(null);
  const [removing, setRemoving] = useState<Site | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: messagingKeys.channels(channel.client_public_id) });
  };
  const failed = (error: unknown) =>
    toast.error('Could not change that', error instanceof ApiError ? error.message : undefined);
  const pause = useMutation({
    mutationFn: ({ site, status }: { site: Site; status: Site['status'] }) =>
      api.patch(`${base}/sites/${site.public_id}`, { status }),
    onSuccess: refresh,
    onError: failed,
  });

  const data = overview.data;
  const full = data ? data.usage.sites >= data.usage.max_sites : false;

  return (
    <Card>
      <CardHeader
        title="Websites and apps"
        description={
          data
            ? `${data.usage.sites} of ${data.usage.max_sites} used · ${data.usage.conversations} conversations this month${
                data.usage.monthly_conversations ? ` of ${data.usage.monthly_conversations}` : ''
              }`
            : undefined
        }
        actions={
          <Can permission="messaging.sender.create">
            <Button size="sm" onClick={() => setEditing('new')} disabled={!data || full}>
              <Plus className="size-3.5" />
              Add a website or app
            </Button>
          </Can>
        }
      />
      <CardBody>
        {overview.data === undefined ? (
          <QueryPending query={overview} label="Loading websites and apps" />
        ) : !data ? (
          <p className="text-sm text-danger">Could not load the websites and apps.</p>
        ) : data.sites.length === 0 ? (
          <p className="text-sm text-muted">
            None yet. Add the client’s website and its domains; the client can also do this from its own portal.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {data.sites.map((site) => (
              <li key={site.public_id} className="space-y-2 py-3 first:pt-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    {site.platform === 'app' ? (
                      <Smartphone className="size-3.5 text-muted" />
                    ) : (
                      <Globe className="size-3.5 text-muted" />
                    )}
                    <span className="text-sm font-medium text-heading">{site.name}</span>
                    {site.status === 'paused' ? (
                      <Badge tone="warning">Paused</Badge>
                    ) : (
                      <Badge tone={HEALTH[site.health.state].tone}>{HEALTH[site.health.state].label}</Badge>
                    )}
                    <span className="text-2xs text-faint">
                      {site.open_conversations} open
                      {site.health.last_seen_at
                        ? ` · seen ${formatDateTime(site.health.last_seen_at)}${
                            site.health.sdk_version ? ` (${site.health.sdk_version})` : ''
                          }`
                        : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Can permission="messaging.sender.edit">
                      <Button variant="secondary" size="sm" aria-label={`Edit ${site.name}`} onClick={() => setEditing(site)}>
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={site.status === 'active' ? `Pause ${site.name}` : `Resume ${site.name}`}
                        loading={pause.isPending && pause.variables?.site.public_id === site.public_id}
                        onClick={() => pause.mutate({ site, status: site.status === 'active' ? 'paused' : 'active' })}
                      >
                        {site.status === 'active' ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
                      </Button>
                    </Can>
                    <Can permission="messaging.sender.delete">
                      <Button variant="secondary" size="sm" aria-label={`Remove ${site.name}`} onClick={() => setRemoving(site)}>
                        <Trash2 className="size-3.5" />
                      </Button>
                    </Can>
                  </div>
                </div>
                <p className="text-xs text-muted">
                  {site.platform === 'app'
                    ? 'A mobile app: no domains to check.'
                    : site.domains.length
                      ? site.domains.join(', ')
                      : 'No domain listed — the widget runs nowhere until one is added.'}
                  {site.health.state === 'refused' && site.health.last_refused_origin
                    ? ` · last refused on ${site.health.last_refused_origin}`
                    : ''}
                </p>
                <pre className="whitespace-pre-wrap break-all rounded-control border border-line bg-surface-2 p-2.5 font-mono text-2xs text-body">
                  {snippet(data, site)}
                </pre>
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      {editing ? (
        <SiteDialog
          base={base}
          site={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      ) : null}
      <Confirm
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name ?? 'this site'}?`}
        message="Its widget stops on every page at once and its open conversations end. What was said stays in the client's Chats."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={async () => {
          if (!removing) return;
          try {
            await api.delete(`${base}/sites/${removing.public_id}`);
            refresh();
          } catch (caught) {
            failed(caught);
          }
        }}
      />
    </Card>
  );
}

function SiteDialog({
  base,
  site,
  onClose,
  onSaved,
}: {
  base: string;
  site: Site | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(site?.name ?? '');
  const [platform, setPlatform] = useState<Site['platform']>(site?.platform ?? 'website');
  const [domains, setDomains] = useState((site?.domains ?? []).join('\n'));
  const save = useMutation({
    mutationFn: () => {
      const listed = domains.split(/[\n,]+/).map((d) => d.trim()).filter(Boolean);
      const body = { name: name.trim(), domains: platform === 'website' ? listed : [] };
      return site
        ? api.patch(`${base}/sites/${site.public_id}`, body)
        : api.post(`${base}/sites`, { ...body, platform });
    },
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error.message : save.error ? 'Could not reach the server.' : null;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdrop={false}
      title={site ? `Edit ${site.name}` : 'Add a website or app'}
      description="How the widget looks and what it says are the client's own, in its portal."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!name.trim()}>
            {site ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <Input label="Name" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
        <Select
          label="Kind"
          value={platform}
          disabled={site !== null}
          options={[
            { value: 'website', label: 'Website' },
            { value: 'app', label: 'Mobile app' },
          ]}
          onChange={(event) => setPlatform(event.target.value as Site['platform'])}
        />
        {platform === 'website' ? (
          <Textarea
            label="Domains"
            rows={3}
            value={domains}
            hint="One per line: example.com, www.example.com, or *.example.com for every subdomain."
            onChange={(event) => setDomains(event.target.value)}
          />
        ) : null}
      </div>
    </Modal>
  );
}
